import { useState } from "react";
import {
  closingRows,
  finishNow,
  goBack,
  goBackOptional,
  goNextRequired,
  initialFlowState,
  markPublished,
  resolveOptional,
  visibleOptionalSteps,
} from "./guidedFlowState";
import "../events/EventForm.css";
import "./primerUso.css";

const errorText = (e) => e?.response?.data?.message || e?.message || "No se pudo guardar. Probá de nuevo.";

/* Two-phase progress: "Para publicar" (required) and "Para completar" (optional). */
export const FlowProgress = ({ state, pasosReq, visibleOpc }) => {
  const reqSeg = pasosReq.map((_, i) => (state.fase !== "req" || i <= state.paso ? "is-done" : ""));
  const opcSeg = visibleOpc.map((p, i) => {
    const e = state.estado[p.id];
    if (e === "hecho") return "is-done";
    if (e === "saltado") return "is-skipped";
    return state.fase === "opc" && i === state.opc ? "is-current" : "";
  });
  return (
    <div className="pu-header">
      <div className="pu-stages" aria-hidden="true">
        <div className="pu-stage" style={{ flex: pasosReq.length }}>
          <small>Para publicar</small>
          <div className="pu-progress">{reqSeg.map((c, i) => <span key={i} className={c} />)}</div>
        </div>
        {visibleOpc.length > 0 && (
          <div className="pu-stage" style={{ flex: visibleOpc.length }}>
            <small>Para completar · opcional</small>
            <div className="pu-progress">{opcSeg.map((c, i) => <span key={i} className={c} />)}</div>
          </div>
        )}
      </div>
      <span className="pu-step-text">
        {state.fase === "req"
          ? `Paso ${state.paso + 1} de ${pasosReq.length}`
          : `Opcional ${state.opc + 1} de ${visibleOpc.length}`}
      </span>
    </div>
  );
};

export const FlowClosing = ({ modulo, rows, extras, onIrSeccion, onIrModulo }) => {
  const hechos = rows.filter((r) => r.estado === "hecho").length;
  return (
    <section className="pu-card" aria-labelledby="pu-cierre-titulo">
      <div>
        <div className="pu-eyebrow">Listo</div>
        <h1 id="pu-cierre-titulo" className="pu-title">
          {hechos === rows.length ? "Configuraste todo" : `Completaste ${hechos} de ${rows.length} pasos opcionales`}
        </h1>
        <p className="pu-lead">
          Lo que saltaste lo completás cuando quieras: cada pendiente te lleva a su sección.
        </p>
      </div>
      <ul className="pu-checklist">
        <li className="is-done">
          <span className="pu-check-txt"><b>Para publicar</b><small>{modulo.resumenReq}</small></span>
          <span className="pu-pill pu-pill--ok">Hecho</span>
        </li>
        {rows.map((r) =>
          r.estado === "hecho" ? (
            <li key={r.id} className="is-done">
              <span className="pu-check-txt"><b>{r.titulo}</b><small>{r.seccion}</small></span>
              <span className="pu-pill pu-pill--ok">Hecho</span>
            </li>
          ) : (
            <li key={r.id}>
              <span className="pu-check-txt">
                <b>{r.titulo}</b>
                <small>{r.estado === "saltado" ? "Saltado" : "Pendiente"} · se completa en {r.seccion}</small>
              </span>
              <button type="button" className="pu-link" onClick={() => onIrSeccion(r)}>
                Completar en {r.seccion}
              </button>
            </li>
          )
        )}
        {extras.map((x) => (
          <li key={x.titulo}>
            <span className="pu-check-txt"><b>{x.titulo}</b><small>{x.sub}</small></span>
          </li>
        ))}
      </ul>
      <div className="pu-actions">
        <span />
        <button type="button" className="ef-btn ef-btn--primary" onClick={onIrModulo}>
          Ir a {modulo.nombre}
        </button>
      </div>
    </section>
  );
};

/**
 * Guided first-run shell. The module (Eventos, Reservas, Canchas) supplies its steps and how to save:
 * - pasosReq / pasosOpc: { id, titulo, bajada, seccion, render({data,set,ctx}), falta(data), si?(data), final? }
 * - onPublicar(data) creates the record and returns a context (ids) for the optional steps.
 * - onGuardarOpc(paso, data, ctx, set) saves one optional step; skipping saves nothing.
 * - accionInicial({data,set}) (optional) renders an action above the first required step only, e.g. copying
 *   the configuration of another local. `ctx.avisos` (optional strings) are what the create step could not do.
 */
const GuidedFlow = ({
  modulo,
  pasosReq,
  pasosOpc,
  datosIniciales,
  onPublicar,
  onGuardarOpc,
  onIrSeccion,
  onIrModulo,
  onCancelar,
  extrasCierre = [],
  avisos = null,
  accionInicial = null,
}) => {
  const [state, setState] = useState(initialFlowState);
  const [data, setData] = useState(datosIniciales);
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setData((d) => ({ ...d, ...(typeof patch === "function" ? patch(d) : patch) }));
  const visibleOpc = visibleOptionalSteps(pasosOpc, data);
  const esReq = state.fase === "req";
  const paso = esReq ? pasosReq[state.paso] : visibleOpc[state.opc];

  const avisosCtx = Array.isArray(ctx?.avisos) ? ctx.avisos : [];

  if (state.fase === "cierre") {
    return (
      <div className="pu-page">
        <div className="pu-bar">
          <div><h2>{modulo.nombre}</h2><p>Configuración inicial terminada</p></div>
          <span className="pu-pill pu-pill--ok">Publicado</span>
        </div>
        <FlowClosing
          modulo={modulo}
          rows={closingRows(visibleOpc, state.estado)}
          extras={[...extrasCierre, ...avisosCtx.map((a) => ({ titulo: "Pendiente de revisar", sub: a }))]}
          onIrSeccion={(row) => onIrSeccion(pasosOpc.find((p) => p.id === row.id), ctx)}
          onIrModulo={() => onIrModulo(ctx)}
        />
      </div>
    );
  }
  if (!paso) return null;

  const falta = paso.falta(data);
  const ultimoOpc = !esReq && state.opc === visibleOpc.length - 1;
  const esFinalReq = esReq && state.paso === pasosReq.length - 1;
  const recienPublicado = !esReq && Object.keys(state.estado).length === 0;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const seguirReq = () => {
    if (!esFinalReq) return setState((s) => goNextRequired(s, pasosReq.length));
    return run(async () => {
      const created = await onPublicar(data);
      setCtx(created);
      setState((s) => markPublished(s, visibleOpc));
    });
  };
  const guardarOpc = () =>
    run(async () => {
      await onGuardarOpc(paso, data, ctx, set);
      setState((s) => resolveOptional(s, visibleOpc, paso.id, "hecho"));
    });
  const saltarOpc = () => setState((s) => resolveOptional(s, visibleOpc, paso.id, "saltado"));

  return (
    <div className="pu-page">
      <div className="pu-bar">
        <div>
          <h2>{modulo.nombre}</h2>
          <p>{esReq ? "Primera vez en el módulo · configuración inicial" : "Configuración inicial · lo que falta"}</p>
        </div>
        {!esReq && <span className="pu-pill pu-pill--ok">Publicado</span>}
      </div>
      {avisos}
      {esReq && state.paso === 0 && accionInicial?.({ data, set })}
      {avisosCtx.length > 0 && (
        <div className="ef-alert ef-alert--warning" role="alert">
          Tu local ya está creado, pero no pudimos copiar todo:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {avisosCtx.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </div>
      )}
      {recienPublicado && (
        <div className="pu-success" role="status">
          {modulo.publicado} Si querés, completá el resto ahora; si no, terminá y lo hacés después desde el formulario.
        </div>
      )}
      {error && <div className="ef-alert ef-alert--danger pu-error" role="alert">{error}</div>}
      <section className="pu-card" aria-labelledby="pu-paso-titulo">
        <FlowProgress state={state} pasosReq={pasosReq} visibleOpc={visibleOpc} />
        <div>
          <div className="pu-eyebrow">{esReq ? "Para publicar" : `Opcional · ${paso.seccion}`}</div>
          <h1 id="pu-paso-titulo" className="pu-title">{paso.titulo}</h1>
          <p className="pu-lead">{paso.bajada}</p>
        </div>
        <div className="pu-body">{paso.render({ data, set, ctx })}</div>
        <div className="pu-actions">
          {esReq ? (
            state.paso > 0 ? (
              <button type="button" className="ef-btn ef-btn--ghost" onClick={() => setState(goBack)} disabled={busy}>Atrás</button>
            ) : (
              <button type="button" className="ef-btn ef-btn--ghost" onClick={onCancelar} disabled={busy}>Cancelar</button>
            )
          ) : (
            <div className="pu-row-actions">
              {state.opc > 0 && (
                <button type="button" className="ef-btn ef-btn--ghost" onClick={() => setState(goBackOptional)} disabled={busy}>Atrás</button>
              )}
              <button type="button" className="ef-btn ef-btn--ghost" onClick={saltarOpc} disabled={busy}>Saltar este paso</button>
            </div>
          )}
          <div className="pu-actions-right">
            <span className="pu-hint" aria-live="polite">{falta}</span>
            {!esReq && !ultimoOpc && (
              <button type="button" className="ef-btn ef-btn--outline" onClick={() => setState(finishNow)} disabled={busy}>
                Terminar ahora
              </button>
            )}
            <button
              type="button"
              className="ef-btn ef-btn--primary"
              disabled={Boolean(falta) || busy}
              onClick={esReq ? seguirReq : guardarOpc}
            >
              {busy ? "Guardando..." : esReq ? paso.final || "Continuar" : ultimoOpc ? "Guardar y terminar" : "Guardar y seguir"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidedFlow;
